import { useState, useEffect, useCallback } from 'react';
import { 
  getRewardsBalance, 
  createWallet as createWalletApi, 
  claimRewards as claimRewardsApi, 
  transferToWallet as transferToWalletApi,
  RewardsBalanceResponse, 
  ClaimRewardsResponse,
  TransferToWalletResponse
} from '@/api/rewards';

interface UseRewardsBalanceReturn {
  balance: number;
  uiBalance: string;
  decimals: number;
  walletAddress: string | null;
  tokenMint: string;
  chain: string;
  claimableRewards: number;
  isLoading: boolean;
  isCreatingWallet: boolean;
  isClaiming: boolean;
  isTransferring: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createWallet: () => Promise<string | null>;
  claimRewards: () => Promise<ClaimRewardsResponse | null>;
  transferToWallet: (destinationAddress: string, amount: number) => Promise<TransferToWalletResponse | null>;
}

export function useRewardsBalance(): UseRewardsBalanceReturn {
  const [data, setData] = useState<RewardsBalanceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getRewardsBalance();
      setData(response);
    } catch (err) {
      console.error('Error fetching rewards balance:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch balance');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createWallet = useCallback(async (): Promise<string | null> => {
    try {
      setIsCreatingWallet(true);
      setError(null);
      const response = await createWalletApi();
      // Refetch balance to update wallet address
      await fetchBalance();
      return response.walletAddress;
    } catch (err) {
      console.error('Error creating wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to create wallet');
      return null;
    } finally {
      setIsCreatingWallet(false);
    }
  }, [fetchBalance]);

  const claimRewards = useCallback(async (): Promise<ClaimRewardsResponse | null> => {
    try {
      setIsClaiming(true);
      setError(null);
      const response = await claimRewardsApi();
      // Refetch balance to update claimable rewards and wallet balance
      await fetchBalance();
      return response;
    } catch (err) {
      console.error('Error claiming rewards:', err);
      setError(err instanceof Error ? err.message : 'Failed to claim rewards');
      return null;
    } finally {
      setIsClaiming(false);
    }
  }, [fetchBalance]);

  const transferToWallet = useCallback(async (
    destinationAddress: string, 
    amount: number
  ): Promise<TransferToWalletResponse | null> => {
    try {
      setIsTransferring(true);
      setError(null);
      const response = await transferToWalletApi({ destinationAddress, amount });
      // Refetch balance to update wallet balance after transfer
      await fetchBalance();
      return response;
    } catch (err) {
      console.error('Error transferring to wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to transfer tokens');
      return null;
    } finally {
      setIsTransferring(false);
    }
  }, [fetchBalance]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  return {
    balance: data?.balance ?? 0,
    uiBalance: data?.uiBalance ?? '0',
    decimals: data?.decimals ?? 9,
    walletAddress: data?.walletAddress ?? null,
    tokenMint: data?.tokenMint ?? '',
    chain: data?.chain ?? 'devnet',
    claimableRewards: data?.claimableRewards ?? 0,
    isLoading,
    isCreatingWallet,
    isClaiming,
    isTransferring,
    error,
    refetch: fetchBalance,
    createWallet,
    claimRewards,
    transferToWallet,
  };
}
